"use client";

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import "./globals.css";
import { recoverFromChunkError } from "@/lib/chunkRecovery";

/**
 * The last line of defence: an error in the root layout itself.
 *
 * This file REPLACES the root layout when it renders, which has consequences
 * worth stating rather than discovering. It has to supply its own <html> and
 * <body>, and it cannot export `metadata` because error boundaries are client
 * components — the title is set with React's <title> instead. It also means
 * nothing from the layout is available here: no fonts, no providers, no
 * ThemeSwitcher. So this deliberately imports only the stylesheet and leans on
 * system fonts.
 *
 * The theme normally lives as `data-theme` on the <html> the layout renders.
 * That <html> is gone, so the stored preference is reapplied after mount —
 * otherwise a writer working in dark mode gets a white flash at the exact
 * moment things are already going wrong.
 */
export default function GlobalError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    console.error("[app] root layout error:", error);
    // A missing chunk means this tab is older than the deploy, not that the
    // app is broken. Fetching the current page fixes it; a 500 does not.
    if (recoverFromChunkError(error)) return;
    Sentry.captureException(error);
    try {
      const stored = localStorage.getItem("nc-theme");
      if (stored === "dark" || stored === "sepia" || stored === "light") setTheme(stored);
    } catch {
      /* ignore */
    }
  }, [error]);

  return (
    <html lang="en" data-theme={theme} className="h-full">
      <body className="min-h-full flex items-center justify-center bg-background text-foreground font-sans antialiased">
        <title>Something went wrong — Novilot</title>
        <main className="w-full max-w-md px-6 py-16 text-center">
          <p className="text-5xl font-semibold tracking-tight tabular-nums text-muted-foreground/30">
            500
          </p>

          <h1 className="mt-3 text-xl font-semibold tracking-tight">Novilot hit a problem</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            The app failed to start this page. Your writing is stored on the server and is not
            affected. Reloading usually clears it.
          </p>

          <div className="mt-7 flex items-center justify-center gap-2">
            <button
              onClick={unstable_retry}
              className="px-4 py-2 rounded-lg bg-accent text-accent-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Try again
            </button>
            <a
              href="/dashboard"
              className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              Go to your projects
            </a>
          </div>

          {error.digest && (
            <p className="mt-8 text-[11px] text-muted-foreground/60">
              Reference <span className="font-mono">{error.digest}</span>
            </p>
          )}
        </main>
      </body>
    </html>
  );
}
